import {useContext, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {
  useParams,
  useRouteMatch,
  useHistory,
  matchPath,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import {Box} from '@mui/material';
import {ContentPage} from '../../../../../../components/content-page/content-page';
import {SectionTabs} from '../../../../../../components/section-tabs/section-tabs';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {translations} from '../../../../../../i18n/translations';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {OfficeContext} from '../../../../../../contexts/office/context';
import {PlaylistsRepository} from '../../../../../../repositories/playlists';
import {PlaylistType} from '../../../../../../backend/enums/playlist-type';
import {AppLanguageTag} from '../../../../../../i18n/types';
import PlaylistEntitiesSystemAdministratorOfficeScreen from './entities/playlist-entities.system-administrator.screen';
import PlaylistItemsSystemAdministratorOfficeScreen from './items/playlist-items.system-administrator.screen';
import PlaylistDetailsSystemAdministratorOfficeScreen from './details/playlist-details.system-administrator.screen';
import PlaylistItemSystemAdministratorOfficeScreen from '../playlist-item/playlist-item.system-administrator.screen';
import i18n from 'i18next';

enum PlaylistSectionTabs {
  Details = 'details',
  Items = 'items',
  Entities = 'entities',
}

enum PathExtensions {
  Items = 'items',
  Item = 'items/:itemId',
  Entities = 'entities',
}

export default function PlaylistSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();
  if (id?.toLowerCase() === 'create') {
    id = undefined;
  }

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Get current tab based on the location
  const handleGetCurrentTab = (pathname: string) => {
    if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Entities}`,
        exact: true,
        strict: false,
      })
    ) {
      return PlaylistSectionTabs.Entities;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Items}`,
        exact: true,
        strict: false,
      })
    ) {
      return PlaylistSectionTabs.Items;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Item}`,
        exact: false, // Not exact, because we will have /items/:itemId/entities
        strict: false,
      })
    ) {
      return undefined;
    }

    // Default
    return PlaylistSectionTabs.Details;
  };

  // Hold state
  const [currentTab, setCurrentTab] = useState<PlaylistSectionTabs | undefined>(
    handleGetCurrentTab(window.location.pathname)
  );

  // Listen for route changes
  useEffect(() => {
    // Listen for route changes
    const routeChangesUnsubscribe = history.listen((location: any) => {
      // Let's update the current tab
      setCurrentTab(handleGetCurrentTab(location.pathname));
    });

    // Clean up
    return () => {
      // Unsubscribe
      routeChangesUnsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, path]);

  // Validate and get the playlist
  useEffect(() => {
    const getPlaylist = async () => {
      if (id) {
        try {
          // Get playlist
          // Note: Don't localize because we need the original model for the playlist form
          const result = await PlaylistsRepository.instance.getPlaylistByIdAsSystemAdministrator(
            id,
            false
          );

          if (result) {
            // Set playlist in context
            officeContext.setPlaylistForm(result);
          } else {
            // Error
            throw new Error('Empty playlist.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get playlist.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(
              translations.playlists.system_administrator.edit.errors.playlist_does_not_exist_error
            ),
          });

          // Redirect
          history.push(`${url.replace(`/${id}`, '')}`);
        }
      } else {
        // Set playlist for create
        officeContext.setPlaylistForm({
          type: PlaylistType.Karaoke,
          isActive: true,
          name: '',
          systemButton: {enabledStateColor: '', pressedStateColor: ''},
        });
      }
    };

    // Get playlist
    getPlaylist();

    // Clean up
    return () => {
      // Clear playlist
      officeContext.clearPlaylistForm();

      // Log
      console.debug('🧹 Playlist form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Get title
  let title: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      title =
        officeContext.state.playlistForm?.nameLocalized?.ptPT ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.esES:
      title =
        officeContext.state.playlistForm?.nameLocalized?.esES ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.glES:
      title =
        officeContext.state.playlistForm?.nameLocalized?.glES ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.enUS:
      title =
        officeContext.state.playlistForm?.nameLocalized?.enUS ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.ptBR:
      title =
        officeContext.state.playlistForm?.nameLocalized?.ptBR ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    default:
      title = officeContext.state.playlistForm?.name || '';
      break;
  }

  return (
    <ContentPage>
      {id && currentTab && (
        <Box sx={{marginBottom: '16px'}}>
          <SectionTabs
            title={title}
            titleAvatar={{
              url: officeContext.state.playlistForm?.imageUrl,
              type: 'other',
            }}
            currentTab={currentTab}
            tabs={[
              {
                value: PlaylistSectionTabs.Details,
                label: t(translations.playlists.system_administrator.edit.tabs.details),
                href: url,
              },
              {
                value: PlaylistSectionTabs.Items,
                label: t(translations.playlists.system_administrator.edit.tabs.items),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Items}`
                    : `${url}/${PathExtensions.Items}`,
              },
              {
                value: PlaylistSectionTabs.Entities,
                label: t(translations.playlists.system_administrator.edit.tabs.entities),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Entities}`
                    : `${url}/${PathExtensions.Entities}`,
              },
            ]}
            exitUrl={`${url.replace(`/${id}`, '')}`}
          />
        </Box>
      )}
      {/* Main content */}
      <Box>
        <Switch>
          {/* Playlist details */}
          <Route exact path={path}>
            <PlaylistDetailsSystemAdministratorOfficeScreen />
          </Route>
          {/* Playlist items */}
          <Route exact path={`${path}/${PathExtensions.Items}`}>
            <PlaylistItemsSystemAdministratorOfficeScreen />
          </Route>
          {/* Playlist entities */}
          <Route exact path={`${path}/${PathExtensions.Entities}`}>
            <PlaylistEntitiesSystemAdministratorOfficeScreen />
          </Route>
          {/* Playlist item - Not exact, because we will have /items/:itemId/entities */}
          <Route path={`${path}/${PathExtensions.Item}`}>
            <PlaylistItemSystemAdministratorOfficeScreen />
          </Route>

          {/** Default route */}
          <Route path={path}>
            <Redirect to={url} />
          </Route>
        </Switch>
      </Box>
    </ContentPage>
  );
}
